export type UsersStatusFilter = 'active' | 'deactivated' | 'deleted' | 'all';

export type UsersDateRange = {
  from: Date | null;
  to: Date | null;
};

export type TeamOption = {
  value: string;
  label: string;
};

type BuildUsersListWhereArgs = {
  searchText: string;
  statusFilter: UsersStatusFilter;
  clientType: string | null;
  selectedTeam: string;
  teamsList: TeamOption[];
  scopedSalesRepId: string | null;
  scopedAffiliateId: string | null;
  isMarketingUser: boolean;
  isAffiliateAdmin: boolean;
  lastActiveRange: UsersDateRange;
  memberSinceRange: UsersDateRange;
  normalizeClientType: (value?: string | null) => string;
};

const toIso = (date: Date, endOfDay = false) => {
  const next = new Date(date);
  if (endOfDay) {
    next.setHours(23, 59, 59, 999);
  } else {
    next.setHours(0, 0, 0, 0);
  }
  return next.toISOString();
};

export const buildUsersListScopeWhere = ({
  scopedSalesRepId,
  scopedAffiliateId,
  isMarketingUser,
  isAffiliateAdmin,
}: Pick<
  BuildUsersListWhereArgs,
  'scopedSalesRepId' | 'scopedAffiliateId' | 'isMarketingUser' | 'isAffiliateAdmin'
>): Record<string, unknown> => {
  const and: Record<string, unknown>[] = [
    { u_deleted_at: { _is_null: true } },
    { u_role: { _eq: 'user' } },
  ];

  if (isMarketingUser) {
    if (scopedSalesRepId) {
      and.push({ u_sales_reps_id: { _eq: scopedSalesRepId } });
    } else {
      and.push({ u_id: { _is_null: true } });
    }
  }

  if (isAffiliateAdmin) {
    if (scopedAffiliateId) {
      and.push({ u_affiliate_id: { _eq: scopedAffiliateId } });
    } else {
      and.push({ u_id: { _is_null: true } });
    }
  }

  return { _and: and };
};

export const buildUsersListBaseWhere = (
  args: Omit<BuildUsersListWhereArgs, 'statusFilter'>
): Record<string, unknown> => buildUsersListWhere({ ...args, statusFilter: 'all' });

export const buildUsersListWhere = ({
  searchText,
  statusFilter,
  clientType,
  selectedTeam,
  teamsList,
  scopedSalesRepId,
  scopedAffiliateId,
  isMarketingUser,
  isAffiliateAdmin,
  lastActiveRange,
  memberSinceRange,
  normalizeClientType,
}: BuildUsersListWhereArgs): Record<string, unknown> => {
  const and: Record<string, unknown>[] = [
    { u_deleted_at: { _is_null: true } },
    { u_role: { _eq: 'user' } },
  ];

  const trimmedSearch = searchText.trim();
  if (trimmedSearch) {
    const ilike = `%${trimmedSearch}%`;
    and.push({
      _or: [
        { u_email_id: { _ilike: ilike } },
        { u_full_name: { _ilike: ilike } },
        { u_phone_number: { _ilike: ilike } },
        { user_referred_to: { ur_joining_code: { _ilike: ilike } } },
      ],
    });
  }

  if (statusFilter === 'active') {
    and.push({ u_is_active: { _eq: true } }, { u_delete_requested: { _eq: false } });
  } else if (statusFilter === 'deactivated') {
    and.push({ u_is_active: { _eq: false } }, { u_delete_requested: { _eq: false } });
  } else if (statusFilter === 'deleted') {
    and.push({ u_delete_requested: { _eq: true } });
  }

  if (clientType && clientType !== 'All') {
    const normalized = normalizeClientType(clientType);
    and.push({
      _or: [
        { u_pricing_mode: { _ilike: normalized } },
        { u_client_type: { _ilike: normalized } },
      ],
    });
  }

  if (isMarketingUser) {
    if (scopedSalesRepId) {
      and.push({ u_sales_reps_id: { _eq: scopedSalesRepId } });
    } else {
      and.push({ u_id: { _is_null: true } });
    }
  }

  if (isAffiliateAdmin) {
    if (scopedAffiliateId) {
      and.push({ u_affiliate_id: { _eq: scopedAffiliateId } });
    } else {
      and.push({ u_id: { _is_null: true } });
    }
  }

  if (lastActiveRange.from) {
    and.push({ u_last_online: { _gte: toIso(lastActiveRange.from) } });
  }
  if (lastActiveRange.to) {
    and.push({ u_last_online: { _lte: toIso(lastActiveRange.to, true) } });
  }
  if (memberSinceRange.from) {
    and.push({ u_created_at: { _gte: toIso(memberSinceRange.from) } });
  }
  if (memberSinceRange.to) {
    and.push({ u_created_at: { _lte: toIso(memberSinceRange.to, true) } });
  }

  if (selectedTeam) {
    const teamConditions: Record<string, unknown>[] = [];
    const selectedTeamLower = String(selectedTeam).toLowerCase();
    const teamObj = teamsList.find((team) => team.value === selectedTeam);
    const label = teamObj?.label || '';
    const nameFromLabel = label.replace(/\s*\(.+\)$/, '').trim();
    const abbrevMatch = label.match(/\(([^)]+)\)$/);
    const abbrevFromLabel = abbrevMatch ? abbrevMatch[1].trim() : '';

    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (uuidPattern.test(selectedTeam)) {
      teamConditions.push({ user_preference: { lc_id: { _eq: selectedTeam } } });
    }

    const namesToMatch = new Set<string>();
    if (nameFromLabel) namesToMatch.add(nameFromLabel);
    if (abbrevFromLabel) namesToMatch.add(abbrevFromLabel);
    if (!teamObj) namesToMatch.add(selectedTeam);

    namesToMatch.forEach((teamName) => {
      teamConditions.push({ u_teams: { _contains: [teamName] } });
      teamConditions.push({
        user_preference: {
          league_category: {
            _or: [
              { lc_name: { _ilike: teamName } },
              { lc_abbrev: { _ilike: teamName } },
            ],
          },
        },
      });
      if (teamName.toLowerCase() !== selectedTeamLower) {
        teamConditions.push({ u_teams: { _contains: [teamName.toLowerCase()] } });
      }
    });

    if (teamConditions.length > 0) {
      and.push({ _or: teamConditions });
    }
  }

  return { _and: and };
};

export const buildUsersListOrderBy = (
  sortColumn: string,
  sortDirection: 'asc' | 'desc'
): Record<string, unknown>[] => {
  const direction = sortDirection;
  switch (sortColumn) {
    case 'name':
      return [{ u_full_name: direction }];
    case 'email':
      return [{ u_email_id: direction }];
    case 'status':
      return [{ u_delete_requested: direction }, { u_is_active: direction }];
    case 'clientType':
      return [{ u_pricing_mode: direction }];
    case 'salesRep':
      return [{ u_sales_reps_id: direction }];
    case 'affiliate':
      return [{ u_affiliate_id: direction }];
    case 'lastActive':
      return [{ u_last_online: direction }];
    case 'memberSince':
      return [{ u_created_at: direction }];
    case 'totalSold':
      return [{ tickets_sold_amount: direction }];
    case 'totalRevenue':
      return [{ u_total_earnings: direction }];
    default:
      return [{ u_last_online: 'desc' }];
  }
};
